import { Helmet } from "react-helmet-async";
import { SITE, abs } from "@/lib/seo";

/**
 * Reusable SEO head manager.
 * props:
 *  - title (page title; brand appended automatically unless titleRaw)
 *  - description
 *  - path (canonical path, e.g. "/indian-sarees")
 *  - image (absolute OG image url)
 *  - type ("website" | "product" | "article")
 *  - noindex (bool)
 *  - jsonLd (object | array of schema objects)
 *  - titleRaw (bool) — use title verbatim without brand suffix
 */
export const Seo = ({
  title,
  description = SITE.description,
  path = "/",
  image = SITE.defaultImage,
  type = "website",
  noindex = false,
  jsonLd,
  titleRaw = false,
}) => {
  const fullTitle = title ? (titleRaw ? title : `${title} | ${SITE.name}`) : `${SITE.name} | ${SITE.tagline}`;
  const canonical = abs(path);
  const schemas = jsonLd ? (Array.isArray(jsonLd) ? jsonLd : [jsonLd]) : [];

  return (
    <Helmet prioritizeSeoTags>
      <title>{fullTitle}</title>
      <meta name="description" content={description} />
      <link rel="canonical" href={canonical} />
      {noindex && <meta name="robots" content="noindex,nofollow" />}

      <meta property="og:type" content={type} />
      <meta property="og:site_name" content={SITE.name} />
      <meta property="og:title" content={fullTitle} />
      <meta property="og:description" content={description} />
      <meta property="og:url" content={canonical} />
      <meta property="og:image" content={image} />

      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={fullTitle} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={image} />

      {schemas.map((s, i) => (
        <script key={i} type="application/ld+json">{JSON.stringify(s)}</script>
      ))}
    </Helmet>
  );
};
